// ─────────────────────────────────────────────
// KLOTHIQ Design Tokens — Status Badge Config
// ─────────────────────────────────────────────

import type { StatusColorKey } from './colors';

export interface StatusBadgeConfig {
  label: string;
  color: StatusColorKey;
}

/**
 * Maps any status string to its badge configuration.
 * Used by the StatusBadge component.
 */
export const STATUS_BADGE_MAP: Record<string, StatusBadgeConfig> = {
  // Sale statuses
  draft: { label: 'Draft', color: 'blue' },
  booked: { label: 'Booked', color: 'amber' },
  confirmed: { label: 'Confirmed', color: 'blue' },
  delivery_pending: { label: 'Delivery Pending', color: 'amber' },
  delivered: { label: 'Delivered', color: 'green' },
  cancelled: { label: 'Cancelled', color: 'red' },
  partially_returned: { label: 'Partially Returned', color: 'purple' },
  returned: { label: 'Returned', color: 'purple' },

  // Return workflow statuses
  return_requested: { label: 'Return Requested', color: 'amber' },
  return_request: { label: 'Return Request', color: 'amber' },
  return_accepted: { label: 'Return Accepted', color: 'blue' },
  return_rejected: { label: 'Return Rejected', color: 'red' },
  return_completed: { label: 'Return Completed', color: 'purple' },
  refund_pending: { label: 'Refund Pending', color: 'amber' },
  refund_completed: { label: 'Refund Completed', color: 'green' },

  // Purchase statuses
  ordered: { label: 'Ordered', color: 'blue' },
  partially_received: { label: 'Partially Received', color: 'amber' },
  received: { label: 'Received', color: 'green' },

  // Payment statuses
  unpaid: { label: 'Unpaid', color: 'red' },
  partially_paid: { label: 'Partially Paid', color: 'amber' },
  paid: { label: 'Paid', color: 'green' },
  payment_pending: { label: 'Payment Pending', color: 'red' },
  payment_completed: { label: 'Payment Completed', color: 'green' },
  partially_refunded: { label: 'Partially Refunded', color: 'amber' },
  refunded: { label: 'Refunded', color: 'grey' },

  // Stock statuses
  in_stock: { label: 'In Stock', color: 'green' },
  low_stock: { label: 'Low Stock', color: 'amber' },
  out_of_stock: { label: 'Out of Stock', color: 'red' },

  // Reservation
  active: { label: 'Active', color: 'green' },
  released: { label: 'Released', color: 'grey' },
  converted: { label: 'Converted', color: 'blue' },

  // General
  inactive: { label: 'Inactive', color: 'grey' },
  voided: { label: 'Voided', color: 'grey' },
  damaged: { label: 'Damaged', color: 'red' },
  overdue: { label: 'Overdue', color: 'red' },
  pending: { label: 'Pending', color: 'amber' },
  in_progress: { label: 'In Progress', color: 'blue' },
};

export function getStatusBadge(status: string): StatusBadgeConfig {
  return (
    STATUS_BADGE_MAP[status] ?? {
      label: status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      color: 'grey' as StatusColorKey,
    }
  );
}
